import React from 'react';
import { NavLink } from 'react-router-dom';
import data from '../testData';
import ImageCarousel from '../components/ImageCarousel';
import ProductInfo from '../components/ProductInfo';
import TeaConnoisseurSection from '../components/TeaConnoisseurSection';
import { menuSlug } from '../utils/menuSlug';
import {
  teaConnoisseurCarouselItems,
  teaEnergyCarouselItems,
  teaFavoritesCarouselItems,
  teaImmunityCarouselItems,
} from '../data/teaData';
import styles from '../styles/Tea.module.css';

const publicUrl = process.env.PUBLIC_URL || '';
const teaMenu = data.data.Tea || [];
const teaCategories = [...new Set(teaMenu.map(tea => tea.category))];

const getTeaImage = tea => `${publicUrl}/images/tea/${tea.picture}.webp`;

class Tea extends React.PureComponent {
  state = {
    searchTerm: '',
    selectedProduct: null,
  };

  getActiveCategory = () => {
    if (typeof window === 'undefined') {
      return '';
    }

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const categorySlug = pathParts[pathParts.length - 1] || '';

    return teaCategories.find(category => (
      menuSlug(category) === categorySlug || category === categorySlug
    )) || '';
  };

  handleSearch = event => {
    this.setState({ searchTerm: event.target.value });
  };

  handleImageClick = tea => {
    this.setState({
      selectedProduct: {
        product: {
          title: tea.name,
          description: tea.body,
          ingredients: tea.contain.join(', '),
          price: tea.price,
        },
        category: tea.category,
        imageSrc: getTeaImage(tea),
      },
    });
  };

  handleCarouselSelect = item => {
    this.setState({
      selectedProduct: {
        product: {
          ...item.product,
          price: item.price,
        },
        category: item.category,
        imageSrc: item.src,
        imageBackground: {
          src: item.src,
          position: item.backgroundPosition,
          size: item.backgroundSize,
        },
      },
    });
  };

  closeProductInfo = () => {
    this.setState({ selectedProduct: null });
  };

  getVisibleMenu = activeCategory => {
    const search = this.state.searchTerm.trim().toLowerCase();
    return teaMenu.filter(tea => {
      const matchesCategory = !activeCategory || tea.category === activeCategory;
      const searchableText = [tea.name, tea.body, tea.category, ...tea.contain]
        .join(' ')
        .toLowerCase();
      return matchesCategory && (!search || searchableText.includes(search));
    });
  };

  renderMenuCard = tea => (
    <article className={styles.menuCard} key={tea.id}>
      <button
        className={styles.cardImageButton}
        type="button"
        onClick={() => this.handleImageClick(tea)}
        aria-label={`Open details for ${tea.name}`}
      >
        <img
          className={styles.cardImage}
          src={getTeaImage(tea)}
          alt={tea.name}
          loading="lazy"
          decoding="async"
        />
        <span className={styles.cardOverlay}>View tea details</span>
      </button>
      <div className={styles.cardBody}>
        <p className={styles.cardCategory}>{tea.category}</p>
        <h2>{tea.name}</h2>
        <p>{tea.body}</p>
        <strong>{tea.price},-</strong>
      </div>
    </article>
  );

  render() {
    const activeCategory = this.getActiveCategory();
    const showAllCarousels = !activeCategory;
    const showConnoisseur = activeCategory === 'За ценители';
    const showImmunity = showAllCarousels || activeCategory === 'За имунитет';
    const showEnergy = showAllCarousels || activeCategory === 'За енергия';
    const visibleMenu = this.getVisibleMenu(activeCategory);
    const showMenuCards = !showConnoisseur;

    return (
      <main className={styles.page}>
        <header
          className={styles.hero}
          style={{ backgroundImage: `url(${publicUrl}/images/hero/tea-page-bg.webp)` }}
        >
          <div className={styles.heroOverlay}>
            <p>Grain Bites</p>
            <h1>Чаят, който всички харесват</h1>
          </div>
        </header>

        <section className={styles.controls} aria-label="Tea menu controls">
          <label className={styles.searchLabel}>
            <input
              type="search"
              aria-label="Search tea"
              value={this.state.searchTerm}
              onChange={this.handleSearch}
              placeholder="Search tea"
            />
          </label>
          <nav className={styles.categories} aria-label="Tea categories">
            {teaCategories.map(category => (
              <NavLink
                key={category}
                to={`${publicUrl}/Tea/${menuSlug(category)}`}
                className={styles.categoryLink}
                activeClassName={styles.activeCategory}
              >
                {category}
              </NavLink>
            ))}
          </nav>
        </section>

        {showConnoisseur && (
          <div className={styles.carouselStack}>
            <ImageCarousel
              title="ЧАЯТ, КОЙТО ВСИЧКИ ХАРЕСВАТ"
              items={teaFavoritesCarouselItems}
              ariaLabel="Tea everyone loves"
              onSelect={this.handleCarouselSelect}
              showPrice
            />
            <ImageCarousel
              title="ЗА ЦЕНИТЕЛИ"
              items={teaConnoisseurCarouselItems}
              ariaLabel="Tea for connoisseurs"
              onSelect={this.handleCarouselSelect}
              showPrice
            />
            <ImageCarousel
              title="ЧАЙ ЗА ИМУНИТЕТ"
              items={teaImmunityCarouselItems}
              ariaLabel="Tea for immunity"
              onSelect={this.handleCarouselSelect}
              showPrice
            />
            <ImageCarousel
              title="ЧАЙ ЗА ЕНЕРГИЯ"
              items={teaEnergyCarouselItems}
              ariaLabel="Tea for energy"
              onSelect={this.handleCarouselSelect}
              showPrice
            />
          </div>
        )}

        {!showConnoisseur && showImmunity && (
          <ImageCarousel
            title="ЧАЙ ЗА ИМУНИТЕТ"
            items={teaImmunityCarouselItems}
            ariaLabel="Tea for immunity"
            onSelect={this.handleCarouselSelect}
            showPrice
          />
        )}

        {!showConnoisseur && showEnergy && (
          <ImageCarousel
            title="ЧАЙ ЗА ЕНЕРГИЯ"
            items={teaEnergyCarouselItems}
            ariaLabel="Tea for energy"
            onSelect={this.handleCarouselSelect}
            showPrice
          />
        )}

        {showConnoisseur && <TeaConnoisseurSection />}

        {showMenuCards && (
          <section className={styles.menuSection} aria-label="Tea menu cards">
            <div className={styles.menuGrid}>
              {visibleMenu.map(this.renderMenuCard)}
            </div>
          </section>
        )}

        {this.state.selectedProduct && (
          <ProductInfo
            product={this.state.selectedProduct.product}
            categoryLabel={this.state.selectedProduct.category}
            imageSrc={this.state.selectedProduct.imageSrc}
            imageBackground={this.state.selectedProduct.imageBackground}
            labels={{ close: 'Close tea details', ingredients: 'Ingredients' }}
            onClose={this.closeProductInfo}
          />
        )}
      </main>
    );
  }
}

export default Tea;
